import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import LayoutWithNavbar from './LayoutWithNavbar';
import {
  fetchStates,
  fetchPaidSummaryByVendor,
  fetchSupplierTypes,
  updateSupplierTypeInGstMaster
} from '../services/api';
import { FaEdit, FaEnvelope } from 'react-icons/fa';
import { FaFileExcel } from 'react-icons/fa';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';


const Reconciled = () => {
  const [states, setStates] = useState([]);
  const [selectedState, setSelectedState] = useState('');
  const [selectedMonth, setSelectedMonth] = useState('');
  const [selectedType, setSelectedType] = useState('');
  const [gstin, setGstin] = useState('');
  const [summaryData, setSummaryData] = useState([]);
  const [supplierTypes, setSupplierTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editModal, setEditModal] = useState({ open: false, gstid: null, selectedType: '' });
  const navigate = useNavigate();

  useEffect(() => {
    const loadInitData = async () => {
      const stateRes = await fetchStates();
      if (stateRes.success) setStates(stateRes.data);

      const supplierRes = await fetchSupplierTypes();
      if (supplierRes.success) setSupplierTypes(supplierRes.data);
    };
    loadInitData();
  }, []);

  const getLast12Months = () => {
    const months = [];
    const today = new Date();
    for (let i = 0; i < 12; i++) {
      const d = new Date(today.getFullYear(), today.getMonth() - i, 1);
      const monthShort = d.toLocaleString('default', { month: 'short' });
      const yearShort = d.getFullYear().toString().slice(-2);
      months.push({ label: `${monthShort}-${d.getFullYear()}`, value: `${monthShort}${yearShort}` });
    }
    return months;
  };

  const handleSearch = async () => {
    setLoading(true);
    const res = await fetchPaidSummaryByVendor({ state: selectedState, month: selectedMonth, gstin, type: selectedType });
    if (res.success) setSummaryData(res.data.summary_by_vendor || []);
    setLoading(false);
  };

  // const handleRowClick = (gstid) => navigate(`/invoice-details/${gstid}`);

  const handleRowClick = (gstid) => {
    const url = `${window.location.origin}/paidinvoice-details/${gstid}?state=${selectedState}`;
    window.open(url, '_blank');
  };
  
  const openEditModal = (gstid, currentTypeName) => {
    const typeObj = supplierTypes.find(t => t.suppliertype === currentTypeName);
    setEditModal({ open: true, gstid, selectedType: typeObj?.id || '' });
  };

  const closeModal = () => setEditModal({ open: false, gstid: null, selectedType: '' });

  const handleModalChange = (e) =>
    setEditModal(prev => ({ ...prev, selectedType: e.target.value }));


  const handleExport = () => {
    if (!summaryData.length) return alert("No data to export");

    const exportData = summaryData.map(row => ({
      VENDOR: row.vendor_name,
      GSTIN: row.gstin,
      BRANCH: row.branch,
      VENDORTYPE: row.vendor_type,
      "TAXABLE VALUE": row.taxable_value,
      CGST: row.cgst,
      SGST: row.sgst,
      IGST: row.igst,
      "INVOICE COUNT": row.invoice_count
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Summary");

    const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    const data = new Blob([excelBuffer], { type: "application/octet-stream" });
    saveAs(data, "Vendor_Invoice_Summary.xlsx");
  };



  const handleSubmitEdit = async () => {
    const res = await updateSupplierTypeInGstMaster(editModal.gstid, editModal.selectedType);
    if (res.success) {
      alert("Supplier type updated successfully.");
      closeModal();
      handleSearch();
    } else {
      alert("Failed to update supplier type.");
    }
  };

  return (
    <LayoutWithNavbar>
      <div style={styles.container}>
        <h2 style={styles.heading}>Vendor Invoice Reconcilled Summary</h2>

        <div style={styles.filterSection}>
          <select value={selectedState} onChange={e => setSelectedState(e.target.value)} style={styles.select}>
            <option value=''>Select State</option>
            {states.map(s => <option key={s.statename} value={s.id}>{s.statename}</option>)}
          </select>

          <select value={selectedType} onChange={e => setSelectedType(e.target.value)} style={styles.select}>
            <option value=''>Select Vendor Type</option>
            {supplierTypes.map(type => (
              <option key={type.id} value={type.suppliertype}>{type.suppliertype}</option>
            ))}
          </select>

          <input type="text" placeholder="GSTIN No" value={gstin} onChange={e => setGstin(e.target.value)} style={styles.input} />
          <button onClick={handleSearch} style={styles.searchButton}>Search</button>
          <button onClick={handleExport} style={styles.exportButton}>
            <FaFileExcel style={{ marginRight: '8px' }} /> Export
          </button>
        </div>

        {loading ? (
          <div style={styles.loaderWrapper}><div className="spinner"></div><p>Loading data...</p></div>
        ) : (
          <div style={styles.tableWrapper}>
            <table style={styles.table}>
              <thead>
                <tr>
                  {['Vendor', 'GSTIN', 'Branch', 'VendorType', 'Taxable Value', 'CGST', 'SGST', 'IGST', 'Invoice Count', 'Actions'].map((col, i) => (
                    <th key={i} style={styles.th}>{col}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {summaryData.map(row => (
                  <tr key={row.gstid} style={styles.tr} onClick={() => handleRowClick(row.gstid)}>
                    <td style={styles.td}>{row.vendor_name}</td>
                    <td style={styles.td}>{row.gstin}</td>
                    <td style={styles.td}>{row.branch}</td>
                    <td style={styles.td}>{row.vendor_type}</td>
                    <td style={styles.td}>{row.taxable_value.toFixed(2)}</td>
                    <td style={styles.td}>{row.cgst.toFixed(2)}</td>
                    <td style={styles.td}>{row.sgst.toFixed(2)}</td>
                    <td style={styles.td}>{row.igst.toFixed(2)}</td>
                    <td style={styles.td}>{row.invoice_count}</td>
                    <td style={{ ...styles.td, display: 'flex', gap: '8px', alignItems: 'center' }} onClick={(e) => e.stopPropagation()}>
                      <button style={styles.iconButton} onClick={() => openEditModal(row.gstid, row.vendor_type)}><FaEdit /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {editModal.open && (
          <div style={styles.modalBackdrop}>
            <div style={styles.modalBox}>
              <h4>Edit Supplier Type</h4>
              <select value={editModal.selectedType} onChange={handleModalChange} style={styles.select}>
                <option value="">Select Type</option>
                {supplierTypes.map(type => (
                  <option key={type.id} value={type.id}>{type.suppliertype}</option>
                ))}
              </select>
              <div style={{ marginTop: '20px' }}>
                <button style={styles.searchButton} onClick={handleSubmitEdit}>Submit</button>
                <button style={{ ...styles.searchButton, backgroundColor: 'gray', marginLeft: '10px' }} onClick={closeModal}>Cancel</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </LayoutWithNavbar>
  );
};

const styles = {
  container: { padding: '30px', backgroundColor: '#f8f9fa', minHeight: '100vh', fontFamily: 'Segoe UI, sans-serif' },
  heading: { fontSize: '24px', fontWeight: '600', marginBottom: '20px' },
  filterSection: { display: 'flex', flexWrap: 'wrap', gap: '12px', marginBottom: '25px', backgroundColor: '#fff', padding: '20px', borderRadius: '8px', boxShadow: '0 0 10px rgba(0,0,0,0.05)' },
  select: { padding: '16px', fontSize: '20px', border: '1px solid #ccc', borderRadius: '6px', flex: '1' },
  input: { padding: '10px', fontSize: '16px', border: '1px solid #ccc', borderRadius: '6px', flex: '2' },
  searchButton: { padding: '10px 20px', fontSize: '16px', fontWeight: '600', backgroundColor: '#007bff', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer' },
  loaderWrapper: { textAlign: 'center', marginTop: '40px' },
  tableWrapper: { overflowX: 'auto', backgroundColor: '#fff', borderRadius: '8px', boxShadow: '0 0 10px rgba(0,0,0,0.04)' },
  table: { width: '100%', borderCollapse: 'collapse', minWidth: '900px' },
  th: { padding: '12px', backgroundColor: '#343a40', color: '#fff', fontWeight: '800', textAlign: 'left', borderBottom: '1px solid #dee2e6' },
  tr: { cursor: 'pointer', transition: 'background-color 0.2s' },
  td: { padding: '12px', borderBottom: '1px solid #dee2e6', fontSize: '17px', backgroundColor: '#fff' },
  iconButton: { display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '6px 10px', fontSize: '14px', backgroundColor: '#ffc107', color: '#000', border: 'none', borderRadius: '4px', cursor: 'pointer' },
  modalBackdrop: { position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 9999 },
  modalBox: { background: '#fff', padding: '30px', borderRadius: '10px', width: '400px', textAlign: 'center' }
};

export default Reconciled;
